const moment = require('moment-timezone');

/**
 * Aracın belirtilen zaman aralığında müsait olup olmadığını kontrol eder
 * Backend'den gelen rezervasyon bilgilerini kullanır
 *
 * @param {Object} vehicle - Araç bilgisi (tracking/getAll'dan gelen rezervasyon alanlarıyla)
 * @param {string} orderStartTime - Siparişin başlangıç zamanı (YYYY-MM-DD HH:mm:ss)
 * @param {string} orderEndTime - Siparişin bitiş zamanı (YYYY-MM-DD HH:mm:ss)
 * @returns {boolean} Araç müsait mi?
 */
function isVehicleAvailableForTimeSlot(vehicle, orderStartTime, orderEndTime) {
  // Eğer araçta rezervasyon yoksa direkt müsait
  if (!vehicle.hasReservation || !vehicle.allReservations || vehicle.allReservations.length === 0) {
    return true;
  }

  // Sipariş zaman aralığı
  const orderStart = moment(orderStartTime);
  const orderEnd = moment(orderEndTime);

  // Geçersiz tarihler
  if (!orderStart.isValid() || !orderEnd.isValid()) {
    console.warn('Geçersiz sipariş zamanı:', { orderStartTime, orderEndTime });
    return false;
  }

  console.log(`⏰ [${vehicle.vehicle}] Zaman slot kontrolü başladı:`, {
    orderStartTime: orderStart.format('YYYY-MM-DD HH:mm'),
    orderEndTime: orderEnd.format('YYYY-MM-DD HH:mm'),
    reservationCount: vehicle.allReservations.length
  });

  // Tüm rezervasyonları kontrol et
  for (const reservation of vehicle.allReservations) {
    const resStart = moment(reservation.scheduledTime);
    let resEnd = moment(reservation.estimatedEndTime);

    // Eğer estimatedEndTime null ise, scheduledTime'a 3 saat ekle
    if (!resEnd.isValid() && resStart.isValid()) {
      resEnd = resStart.clone().add(3, 'hours');
      console.log(`⚠️ estimatedEndTime null, otomatik hesaplandı: ${resEnd.format('YYYY-MM-DD HH:mm')}`);
    }

    // Geçersiz rezervasyon zamanı
    if (!resStart.isValid() || !resEnd.isValid()) {
      console.warn('Geçersiz rezervasyon zamanı:', reservation);
      continue;
    }

    // Zaman çakışması kontrolü
    // Çakışma var = sipariş başlangıcı rezervasyon bitişinden önce VE sipariş bitişi rezervasyon başlangıcından sonra
    const beforeCheck = orderStart.isBefore(resEnd);
    const afterCheck = orderEnd.isAfter(resStart);
    const hasOverlap = beforeCheck && afterCheck;

    console.log(`📋 [${vehicle.vehicle}] Rezervasyon #${reservation.orderNo} kontrol:`, {
      orderSlot: `${orderStart.format('DD MMM HH:mm')} → ${orderEnd.format('DD MMM HH:mm')}`,
      resSlot: `${resStart.format('DD MMM HH:mm')} → ${resEnd.format('DD MMM HH:mm')}`,
      checks: {
        'orderStart < resEnd': beforeCheck,
        'orderEnd > resStart': afterCheck,
        hasOverlap
      }
    });

    if (hasOverlap) {
      console.log(`❌ Zaman çakışması tespit edildi!`);
      return false; // Çakışma var, araç müsait değil
    }
  }

  // Hiçbir rezervasyonla çakışma yok
  console.log(`✅ [${vehicle.vehicle}] Zaman slotu müsait - çakışma yok`);
  return true;
}

/**
 * Siparişin tahmini bitiş zamanını hesaplar
 *
 * @param {string} startTime - Başlangıç zamanı (YYYY-MM-DD HH:mm:ss veya YYYY-MM-DD)
 * @param {number} durationHours - Süre (saat cinsinden, varsayılan: 3 saat)
 * @returns {string} Bitiş zamanı (YYYY-MM-DD HH:mm:ss)
 */
function calculateEstimatedEndTime(startTime, durationHours = 3) {
  const start = moment(startTime);

  if (!start.isValid()) {
    console.warn('Geçersiz başlangıç zamanı:', startTime);
    return moment().add(durationHours, 'hours').format('YYYY-MM-DD HH:mm:ss');
  }

  return start.add(durationHours, 'hours').format('YYYY-MM-DD HH:mm:ss');
}

/**
 * Araçları zaman müsaitliğine göre filtreler
 * Sequential (zincirleme) işleri de dikkate alır
 *
 * @param {Array} vehicles - Araç listesi (tracking/getAll'dan gelen)
 * @param {string} orderStartTime - Siparişin başlangıç zamanı
 * @param {string} orderEndTime - Siparişin bitiş zamanı
 * @param {Object} order - Sipariş bilgisi (sequential kontrolü için)
 * @returns {Array} Müsait araçlar
 */
function filterAvailableVehicles(vehicles, orderStartTime, orderEndTime, order = null) {
  if (!vehicles || vehicles.length === 0) {
    return [];
  }

  return vehicles.filter(vehicle => {
    // Normal zaman kontrolü
    const isAvailable = isVehicleAvailableForTimeSlot(vehicle, orderStartTime, orderEndTime);

    // Eğer müsait değilse ama sequential olabilecekse kontrol et
    if (!isAvailable && order && vehicle.allReservations && vehicle.allReservations.length === 1) {
      // Sequential kontrolü yap
      const { isSequential } = require('../controller/isSequential');

      const orderForSequential = {
        ...order,
        orderNo: order.orderNo || order.no, // Explicitly pass orderNo
        startLat: order.outpointLat || order.startLat,
        startLng: order.outpointLng || order.startLng,
        endLat: order.targetPointLat || order.endLat,
        endLng: order.targetPointLng || order.endLng,
      };

      // Eğer sequential ise aracı dahil et
      if (isSequential(vehicle, orderForSequential)) {
        console.log(`Sequential iş tespit edildi: Araç ${vehicle.vehicle} için #${order.orderNo}`);
        return true;
      }
    }

    return isAvailable;
  });
}

/**
 * Aracın rezervasyon durumu hakkında detaylı bilgi döndürür
 *
 * @param {Object} vehicle - Araç bilgisi
 * @param {string} orderStartTime - Siparişin başlangıç zamanı
 * @param {string} orderEndTime - Siparişin bitiş zamanı
 * @returns {Object} Rezervasyon durumu bilgisi
 */
function getReservationConflictInfo(vehicle, orderStartTime, orderEndTime) {
  if (!vehicle.hasReservation || !vehicle.allReservations || vehicle.allReservations.length === 0) {
    return {
      hasConflict: false,
      conflictingReservations: [],
      message: 'Araç müsait'
    };
  }

  const orderStart = moment(orderStartTime);
  const orderEnd = moment(orderEndTime);
  const conflictingReservations = [];

  for (const reservation of vehicle.allReservations) {
    const resStart = moment(reservation.scheduledTime);
    const resEnd = moment(reservation.estimatedEndTime);

    if (!resStart.isValid() || !resEnd.isValid()) continue;

    const hasOverlap = orderStart.isBefore(resEnd) && orderEnd.isAfter(resStart);

    if (hasOverlap) {
      conflictingReservations.push({
        orderNo: reservation.orderNo,
        scheduledTime: reservation.scheduledTime,
        estimatedEndTime: reservation.estimatedEndTime,
        moduleType: reservation.moduleType,
        overlapStart: moment.max(orderStart, resStart).format('YYYY-MM-DD HH:mm'),
        overlapEnd: moment.min(orderEnd, resEnd).format('YYYY-MM-DD HH:mm')
      });
    }
  }

  if (conflictingReservations.length > 0) {
    return {
      hasConflict: true,
      conflictingReservations,
      message: `${conflictingReservations.length} rezervasyonla çakışma var: ${conflictingReservations.map(r => r.orderNo).join(', ')}`
    };
  }

  return {
    hasConflict: false,
    conflictingReservations: [],
    message: 'Araç müsait'
  };
}

module.exports = {
  isVehicleAvailableForTimeSlot,
  calculateEstimatedEndTime,
  filterAvailableVehicles,
  getReservationConflictInfo
};
